import {
  AlertSpg,
  getFieldError,
  GridContainer,
  GridItem,
  InputFormField,
  isEmpty,
  isNotEmpty,
  NacidTextField,
  shouldShowFieldError,
} from "@duosoftbg/nacid-components";
import ArrayFormControl from "../../../../common/form/ArrayFormControl";
import { initialStudiedSubjects } from "../../../../../../../init/seRecognitionInitialValues";
import React, { useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import SelectOrWriteAutocompleteFormField from "../../../../../common/form/SelectOrWriteAutocompleteFormField";
import { getSubjectBgByName } from "../../../../../../../services/schoolSubjectCalls";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../store/redux/store";

export interface StudiedSubjectFormFieldsProps {
  infoText?: string;
  baseField: string;
  includesClassYear?: boolean;
  isSubjectsDataRequired?: boolean;
}

const StudiedSubjectFormFields = ({
  infoText = undefined,
  baseField,
  includesClassYear = false,
  isSubjectsDataRequired = true,
}: StudiedSubjectFormFieldsProps) => {
  const { t } = useTranslation();

  return (
    <>
      {infoText && (
        <GridContainer spacing={3} mt={0}>
          <GridItem xs={12} sm={12} md={12}>
            <AlertSpg severity={"info"}>{t(infoText)}</AlertSpg>
          </GridItem>
        </GridContainer>
      )}

      <ArrayFormControl
        field={baseField}
        initialValues={initialStudiedSubjects}
        renderFormFields={(index, key) => {
          return (
            <FormFields
              index={index}
              key={key}
              baseField={baseField}
              includesClassYear={includesClassYear}
              isSubjectsDataRequired={isSubjectsDataRequired}
            />
          );
        }}
        addBtnLabelCode={"l.btn.studiedSubject.add"}
      />
    </>
  );
};

const FormFields = ({ index, baseField, includesClassYear, isSubjectsDataRequired }) => {
  const { t } = useTranslation();
  const { control, formState, getFieldState } = useFormContext();
  const [warnings, setWarnings] = useState("");
  const gradingScaleDetails = useSelector((state: RootState) => state.AppData.gradingScaleDetails.data);
  const value = useWatch({ control, name: `${baseField}.${index}.diplomaGrade` });
  useEffect(() => {
    if (gradingScaleDetails) {
      handleChange({ target: { name: `${baseField}.${index}.diplomaGrade`, value } });
    }
  }, [gradingScaleDetails]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const check = (type: string): boolean => {
      if (isEmpty(value)) {
        return true;
      }
      if (type === "LETTER") {
        return gradingScaleDetails.some((detail) => detail.symbolValues.toLowerCase() === value.toLowerCase());
      }
      if (type === "NUMERIC" || type === "PERCENT") {
        const numericGrade = parseFloat(value);
        if (isNaN(numericGrade)) {
          return false;
        }
        return gradingScaleDetails.some((detail) => detail.minValue <= numericGrade && numericGrade <= detail.maxValue);
      }
      return true;
    };
    let type = undefined;
    if (gradingScaleDetails.length > 0) {
      type = gradingScaleDetails[0].gradingScale.scaleType;
    }
    if (isNotEmpty(value) && !check(type)) {
      setWarnings(t("m.validation.warnings.gradeNotInScale"));
    } else {
      setWarnings(undefined);
    }
  };

  const subjectFieldsGridSize = includesClassYear ? 4 : 5;

  return (
    <GridContainer spacing={4} mt={0}>
      <GridItem md={subjectFieldsGridSize}>
        <TranslatedSubjectNameAutocompleteFormField
          index={index}
          baseField={baseField}
          isRequired={isSubjectsDataRequired}
        />
      </GridItem>
      <GridItem md={subjectFieldsGridSize}>
        <InputFormField required={false} fieldName={`${baseField}.${index}.subjectName`} labelCode={"l.subjectName"} />
      </GridItem>
      {includesClassYear && (
        <GridItem md={2}>
          <InputFormField
            required={false}
            fieldName={`${baseField}.${index}.classYear`}
            labelCode={"l.additional.subject.class.year"}
          />
        </GridItem>
      )}
      <GridItem md={2}>
        <Controller
          name={`${baseField}.${index}.diplomaGrade`}
          control={control}
          render={({ field }) => {
            const { value, onChange, ...others } = field;
            const valueRevised = undefined === value || null === value ? "" : value;
            return (
              <NacidTextField
                InputLabelProps={{ required: isSubjectsDataRequired }}
                InputProps={{ type: "text" }}
                fullWidth
                size={"small"}
                value={valueRevised}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: warnings ? "orange" : undefined,
                    },
                    "&:hover fieldset": {
                      borderColor: warnings ? "orange" : undefined,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: warnings ? "#ed6c02" : undefined,
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiInputLabel-root": {
                    color: warnings ? "#ed6c02" : undefined,
                  },
                }}
                {...others}
                label={t("l.diplomaGrade")}
                error={shouldShowFieldError(`${baseField}.${index}.diplomaGrade`, formState, getFieldState)}
                helperText={
                  shouldShowFieldError(`${baseField}.${index}.diplomaGrade`, formState, getFieldState) ? (
                    getFieldError(`${baseField}.${index}.diplomaGrade`, getFieldState)
                  ) : warnings ? (
                    <span style={{ color: "#ed6c02" }}>{warnings}</span>
                  ) : null
                }
                onChange={(e) => {
                  onChange(e);
                  handleChange(e);
                }}
              />
            );
          }}
        />
      </GridItem>
    </GridContainer>
  );
};

const TranslatedSubjectNameAutocompleteFormField = ({ index, baseField, isRequired = true }) => {
  const name = useWatch({ name: `${baseField}.${index}.translatedSubjectName` }) || "";

  return (
    <SelectOrWriteAutocompleteFormField
      autocompleteFn={getSubjectBgByName}
      textFieldName={`${baseField}.${index}.translatedSubjectName`}
      required={isRequired}
      label={"l.translatedSubjectName"}
      inputMinSearchLength={1}
      setOptionText={(option) => option.name}
      getOptionLabel={(option) => option.id + ""}
      setInputOnSelect={(option) => option.name}
      selectedOption={{ id: name, name: name }}
    />
  );
};
export default StudiedSubjectFormFields;
